// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    canSubmitForApprovalOrder,
    getCurrentCartState,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import translate from "@insite/client-framework/Translate";
import {
    BillToModel,
    CarrierDto,
    CreditCardBillingAddressDto,
    ShipToModel,
    ShipViaDto,
} from "@insite/client-framework/Types/ApiModels";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import BillingAddressInfoDisplay, {
    BillingAddressInfoDisplayStyles,
} from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/BillingAddressInfoDisplay";
import ShippingAddressInfoDisplay, {
    ShippingAddressInfoDisplayStyles,
} from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/ShippingAddressInfoDisplay";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CheckoutSectionHeading, {
    CheckoutSectionHeadingStyles,
} from "@keller/App/Components/Checkout/CheckoutSectionHeading";
import Seperator from "@keller/App/Components/Common/SharedPrintStyles";
import { CHECKOUT_STEPS } from "@keller/App/Widgets/CheckoutStepper/CheckoutStepper.helper";
import { StepperContext } from "@keller/App/Widgets/CheckoutStepper/StepperContext";
import CreditCardAddressInfoDisplay from "@keller/App/Widgets/OrderConfirmation/CreditCardAddressInfoDisplay";
import { paymentMethodValueDisplay } from "@keller/Overrides/Widgets/CheckoutReviewAndSubmit/checkoutReviewAndSubmit.helper";
import { checkoutReviewAndSubmitStyle } from "@keller/Overrides/Widgets/CheckoutReviewAndSubmit/checkoutReviewAndSubmit.shared.styles";
import { EMPTY_VALUE } from "@keller/XDL/constants";
import { doNothing } from "@keller/XDL/Utilities";
import React, { useContext } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    billTo: BillToModel;
    shipTo: ShipToModel;
    carrier: CarrierDto;
    shipVia: ShipViaDto;
    deliveryDate: Date | null;
    onEditBillTo: () => void;
    onEditShipTo: () => void;
    extendedStyles?: BillingAndShippingAddressStyles;
    notes?: string;
    // XNG-Changes
    canGoBack?: boolean;
    creditCardBillInformation: CreditCardBillingAddressDto;
}
// xng-change
const mapStateToProps = (state: ApplicationState) => ({
    isOrderConfirmationPage: getCurrentPage(state).type === "OrderConfirmationPage",
    richContentWidget: getWidgetsByPageId(state, getCurrentPage(state).id).find((widget: WidgetProps) => {
        return widget.type === "Basic/RichContent";
    })?.fields,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface BillingAndShippingAddressStyles {
    container?: GridContainerProps;
    carrierGridItem?: GridItemProps;
    carrierHeadingText?: TypographyPresentationProps;
    carrierText?: TypographyPresentationProps;
    shippingServiceGridItem?: GridItemProps;
    shippingServiceHeadingText?: TypographyPresentationProps;
    shippingServiceText?: TypographyPresentationProps;
    deliveryDateGridItem?: GridItemProps;
    deliveryDateHeadingText?: TypographyPresentationProps;
    deliveryDateText?: TypographyPresentationProps;
    shippingAddressGridItem?: GridItemProps;
    shippingAddress?: ShippingAddressInfoDisplayStyles;
    billingAddressGridItem?: GridItemProps;
    billingAddress?: BillingAddressInfoDisplayStyles;
    // XNG-Changes
    mainContainer?: GridContainerProps;
    addressGridWrapper?: GridItemProps;
    gridItem?: GridItemProps;
    doNoShipBeforeGridItem?: GridItemProps;
    notesGridItem?: GridItemProps;
    hideOnPrint?: GridItemProps;
    labelName?: CheckoutSectionHeadingStyles;
    orderConfirmationTotalGridItem?: GridItemProps;
    richContentOnPrintGridItem?: GridItemProps;
}

export const billingAndShippingAddressStyles: BillingAndShippingAddressStyles = {
    carrierGridItem: {
        width: [6, 6, 6, 3, 3],
        css: css`
            flex-direction: column;
        `,
    },
    shippingServiceHeadingText: {
        ...checkoutReviewAndSubmitStyle.reviewSection.label,
    },
    shippingServiceText: {
        ...checkoutReviewAndSubmitStyle.reviewSection.value,
    },
    carrierHeadingText: { ...checkoutReviewAndSubmitStyle.reviewSection.label },
    carrierText: {
        ...checkoutReviewAndSubmitStyle.reviewSection.value,
    },
    deliveryDateGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            flex-direction: column;
        `,
    },
    deliveryDateHeadingText: {
        ...checkoutReviewAndSubmitStyle.reviewSection.label,
    },
    deliveryDateText: {
        ...checkoutReviewAndSubmitStyle.reviewSection.value,
    },
    gridItem: {
        ...checkoutReviewAndSubmitStyle.reviewSection.gridItem,
    },
    doNoShipBeforeGridItem: {
        ...checkoutReviewAndSubmitStyle.reviewSection.gridItem,
        width: [12, 12, 5, 5, 4],
    },
    billingAddress: {
        container: {
            gap: 8,
        },
    },
    shippingAddress: {
        container: {
            gap: 8,
        },
    },
    richContentOnPrintGridItem: {
        width: 12,
        css: css`
            &&& {
                display: none;
                @media print {
                    display: block;
                }
            }
        `,
    },
};

const BillingAndShippingInfo = ({
    billTo,
    shipTo,
    carrier,
    shipVia,
    deliveryDate,
    onEditBillTo,
    onEditShipTo,
    extendedStyles,
    notes,
    canGoBack = true,
    creditCardBillInformation,
    // xng-change
    isOrderConfirmationPage,
    richContentWidget,
}: Props) => {
    const styles = mergeToNew(billingAndShippingAddressStyles, extendedStyles);
    // <XNG-Changes>
    const { checkoutInfoState } = useContext(StepperContext);
    const requiresApproval = useAppSelector(state => canSubmitForApprovalOrder(getCurrentCartState(state).value));
    // </XNG-Changes>

    // XNG-Changes: Update structure
    return (
        <GridContainer {...styles.mainContainer}>
            {/* Addresses section */}
            <GridItem {...styles.addressGridWrapper}>
                <GridContainer {...styles.container}>
                    <CheckoutSectionHeading
                        extendedStyles={styles.labelName}
                        label={translate("Addresses")}
                        moveToStep={canGoBack ? CHECKOUT_STEPS.ADDRESS : undefined}
                    />
                    <GridItem {...styles.billingAddressGridItem}>
                        <BillingAddressInfoDisplay
                            billTo={billTo}
                            // XNG-Changes: set doNothing
                            onEdit={doNothing}
                            extendedStyles={styles.billingAddress}
                        />
                    </GridItem>

                    <GridItem {...styles.shippingAddressGridItem}>
                        <ShippingAddressInfoDisplay
                            shipTo={shipTo}
                            // XNG-Changes: set doNothing
                            onEdit={doNothing}
                            extendedStyles={styles.shippingAddress}
                        />
                    </GridItem>

                    {/* xng-change Rich Content Widget for Order Confirmation Page */}
                    {isOrderConfirmationPage && richContentWidget && (
                        <GridItem {...styles.richContentOnPrintGridItem}>
                            <StyledWrapper>
                                {createWidgetElement("Basic/RichContent", { fields: richContentWidget })}
                            </StyledWrapper>
                        </GridItem>
                    )}
                    {creditCardBillInformation && (
                        <GridItem {...styles.gridItem} data-test-selector="creditCardBillingAddress">
                            <CreditCardAddressInfoDisplay
                                extendedStyles={styles.shippingAddress}
                                creditCardBillInformation={creditCardBillInformation}
                            />
                        </GridItem>
                    )}

                    <GridItem {...styles.notesGridItem}>
                        <Typography {...styles.shippingServiceHeadingText}>{translate("Order Notes")}</Typography>
                        <Typography {...styles.shippingServiceText}>{notes || EMPTY_VALUE}</Typography>
                    </GridItem>
                </GridContainer>
            </GridItem>
            {/* <Xng-Changes> */}

            {/* XNG-Changes: show order total summary on print */}
            {isOrderConfirmationPage && (
                <GridItem {...styles.orderConfirmationTotalGridItem}>
                    <StyledWrapper>
                        {createWidgetElement("OrderConfirmation/OrderConfirmationTotal", { fields: {} })}
                    </StyledWrapper>
                </GridItem>
            )}
            <Seperator />
            {/* </Xng-Changes> */}

            {/* Shipping Section */}
            {/* </Xng-Changes> */}
            {/* Wrap Shipping Section */}
            <GridItem {...styles.hideOnPrint}>
                <GridContainer gap={16}>
                    <CheckoutSectionHeading
                        label={translate("Shipping")}
                        moveToStep={canGoBack ? CHECKOUT_STEPS.SHIPPING : undefined}
                    />
                    {/* </Xng-Changes> */}
                    {/* Wrap Shipping Items */}
                    <GridItem {...styles.gridItem}>
                        <Typography {...styles.carrierHeadingText}>{translate("Shipping Method")}</Typography>
                        <Typography {...styles.carrierText}>{carrier?.description || EMPTY_VALUE}</Typography>
                    </GridItem>

                    {/* Xng-Changes: not needed as the design */}
                    {/* <GridItem {...styles.shippingServiceGridItem}>
                        <Typography {...styles.shippingServiceHeadingText}>{translate("Service")}</Typography>
                        <Typography {...styles.shippingServiceText}>{shipVia?.description}</Typography>
                    </GridItem> */}
                    <GridItem {...styles.gridItem}>
                        {/*
                            Don't wrap this check around the parent GridItem because
                            it keeps the billing and shipping address in a separate row.
                        */}
                        {deliveryDate && (
                            <>
                                <Typography {...styles.deliveryDateHeadingText}>
                                    {translate("Requested by Date")}
                                </Typography>
                                <Typography
                                    {...styles.deliveryDateText}
                                    data-test-selector="requestedDeliveryDateValue"
                                >
                                    <LocalizedDateTime dateTime={deliveryDate} />
                                </Typography>
                            </>
                        )}
                    </GridItem>
                    {checkoutInfoState.doNotShipBeforeDate && (
                        <GridItem {...styles.doNoShipBeforeGridItem}>
                            <Typography {...styles.deliveryDateHeadingText}>
                                {translate("Do not shipping before Date")}
                            </Typography>
                            <Typography {...styles.deliveryDateText} data-test-selector="doNotShipBeforeDate">
                                <LocalizedDateTime dateTime={checkoutInfoState.doNotShipBeforeDate} />
                            </Typography>
                        </GridItem>
                    )}
                    {/* <Xng-Changes> */}
                    <Seperator />
                </GridContainer>
                {/* </Xng-Changes> */}
            </GridItem>

            {/* Payment Section */}
            {/* </Xng-Changes> */}
            {!requiresApproval ? (
                <GridItem {...styles.hideOnPrint}>
                    <GridContainer {...styles.mainContainer}>
                        <CheckoutSectionHeading
                            label={translate("Payment")}
                            moveToStep={canGoBack ? CHECKOUT_STEPS.PAYMENT : undefined}
                        />
                        <GridItem {...styles.gridItem}>
                            <Typography {...styles.carrierHeadingText}>
                                {checkoutInfoState.isCreditCard
                                    ? translate("Credit Card")
                                    : translate("Payment Method")}
                            </Typography>
                            <Typography {...styles.carrierText}>
                                {paymentMethodValueDisplay(checkoutInfoState) || EMPTY_VALUE}
                            </Typography>
                        </GridItem>
                        <GridItem {...styles.gridItem}>
                            <Typography {...styles.shippingServiceHeadingText}>
                                {translate("Purchase Order")}
                            </Typography>
                            <Typography {...styles.shippingServiceText}>
                                {checkoutInfoState.poNumber || EMPTY_VALUE}
                            </Typography>
                        </GridItem>
                        <GridItem {...styles.gridItem}>
                            <Typography {...styles.shippingServiceHeadingText}>
                                {translate("Release Number")}
                            </Typography>
                            <Typography {...styles.shippingServiceText}>
                                {checkoutInfoState.releaseNumber || EMPTY_VALUE}
                            </Typography>
                        </GridItem>
                    </GridContainer>
                </GridItem>
            ) : null}
        </GridContainer>
    );
};

export default connect(mapStateToProps)(BillingAndShippingInfo);
